import React, { useState, useEffect } from 'react';
import './App.css';
import GameBoard from './components/GameBoard';
import ScoreCard from './components/ScoreCard';
import EffectsOverlay from './components/EffectsOverlay';
import HelpDialog from './components/HelpDialog';
import DebugPage from './components/DebugPage';
import MobileOptionsModal from './components/MobileOptionsModal';
import { playMeowExcited, playMeowHappy, playPurrContent, playPurrRoll, playPurrLoud, playMeowSurprise } from './utils/audioUtils';

function App() {
  // All hooks must be declared before any conditional logic
  const [gameState, setGameState] = useState({
    currentPlayer: 1,
    player1Score: {},
    player2Score: {},
    dice: Array(5).fill(null),
    held: Array(5).fill(false),
    rollsLeft: 3,
    gameOver: false,
    hasRolled: false
  });

  const [currentEffect, setCurrentEffect] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [lastScoringAction, setLastScoringAction] = useState(null);
  const [backgroundTheme, setBackgroundTheme] = useState('space1');
  const [showMobileOptions, setShowMobileOptions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Theme configurations - defined early to avoid reference errors
  const backgroundThemes = {
    default: {
      name: 'Cosmic Cats',
      backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      cats: [
        '/cat-images/cat-fly-1.png',
        '/cat-images/cat-fly-2.webp', 
        '/cat-images/cat-fly-3.jpeg',
        '/cat-images/cat-surprise.webp',
        '/cat-images/cat-high-5.webp'
      ],
      emojis: ['⭐', '🌟', '✨', '🌙', '🪐'],
      pattern: 'drift'
    },
    ocean: {
      name: 'Ocean Adventure',
      backgroundColor: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 50%, #2d3436 100%)',
      cats: [
        '/cat-images/cat-fly-1.png',
        '/cat-images/cat-fly-2.webp',
        '/cat-images/cat-surprise.webp'
      ],
      emojis: ['🌊', '🐠', '🐟', '🦈', '🐙', '⚓', '🏝️'],
      pattern: 'wave'
    },
    forest: {
      name: 'Magical Forest',
      backgroundColor: 'linear-gradient(135deg, #00b894 0%, #00a085 50%, #2d3436 100%)',
      cats: [
        '/cat-images/cat-head-over-table.png',
        '/cat-images/cat-laydown.webp',
        '/cat-images/cat-surprise.webp'
      ],
      emojis: ['🌸', '🦋', '🍄', '🌿', '🦜', '🐛', '🌺'],
      pattern: 'flutter'
    },
    space1: {
      name: 'Space Convoy',
      backgroundColor: 'linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #000000 100%)',
      cats: [
        '/cat-images/cat-fly-3.png',
        '/cat-images/cat-high-5.png',
        '/cat-images/cat-fly-1.png'
      ],
      emojis: ['🚀', '👽', '🛸', '🌌', '☄️', '🪐', '⚡'],
      pattern: 'convoy'
    },
    space2: {
      name: 'Nebula Storm',
      backgroundColor: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 50%, #1a237e 100%)',
      cats: [
        '/cat-images/cat-surprise.png',
        '/cat-images/cat-back.png',
        '/cat-images/cat-laydown.png'
      ],
      emojis: ['⚡', '🌟', '💫', '🌠', '✨', '🔮', '🌀'],
      pattern: 'storm'
    },
    space3: {
      name: 'Asteroid Field',
      backgroundColor: 'linear-gradient(135deg, #bf360c 0%, #d84315 50%, #1b1b1b 100%)',
      cats: [
        '/cat-images/cat-head-over-table.png',
        '/cat-images/cat-fly-2.webp',
        '/cat-images/cat-fly-1.png'
      ],
      emojis: ['☄️', '🪨', '💥', '🔥', '⭐', '🌑', '💎'],
      pattern: 'asteroid'
    }
  };

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simple routing - check if we're on debug page
  const isDebugPage = window.location.pathname === '/debug' || window.location.hash === '#debug';
  
  if (isDebugPage) {
    return <DebugPage backgroundTheme={backgroundTheme} setBackgroundTheme={setBackgroundTheme} backgroundThemes={backgroundThemes} />;
  }

  const handleEffectComplete = () => {
    setCurrentEffect(null);
  };

  const resetGame = () => {
    // Play excited meow when starting new game
    playMeowExcited();
    
    setGameState({
      currentPlayer: 1,
      player1Score: {},
      player2Score: {},
      dice: Array(5).fill(null),
      held: Array(5).fill(false),
      rollsLeft: 3,
      gameOver: false,
      hasRolled: false
    });
    
    // Reset other states
    setCurrentEffect(null);
    setLastScoringAction(null);
  };

  const undoLastScore = () => {
    if (lastScoringAction) {
      // Restore the complete previous game state including dice configuration
      setGameState(lastScoringAction.gameState);
      
      // Clear the undo action since it's been used
      setLastScoringAction(null);
      
      // Play content purr for undo
      playPurrContent();
    }
  };

  const handleMobileHelp = () => {
    setShowMobileOptions(false);
    setShowHelp(true);
  };

  const handleMobileUndo = () => {
    setShowMobileOptions(false);
    undoLastScore();
  };

  const handleMobileNewGame = () => {
    setShowMobileOptions(false);
    resetGame();
  };

  const rollDice = () => {
    if (gameState.rollsLeft > 0) {
      // Clear undo action when rolling (prevents undoing after rolling)
      if (gameState.rollsLeft === 3) {
        setLastScoringAction(null);
      }
      
      // Play rolling sound
      playPurrRoll();
      
      setGameState(prevState => ({
        ...prevState,
        dice: prevState.dice.map((die, i) => 
          prevState.held[i] ? die : Math.floor(Math.random() * 6) + 1
        ),
        rollsLeft: prevState.rollsLeft - 1,
        hasRolled: true
      }));
    }
  };

  const toggleHold = (index) => {
    if (gameState.rollsLeft < 3) {
      setGameState(prevState => {
        const newHeld = [...prevState.held];
        newHeld[index] = !newHeld[index];
        return { ...prevState, held: newHeld };
      });
    }
  };


  const checkAchievements = (totalScore) => {
    if (totalScore >= 300) {
      setTimeout(() => {
        setCurrentEffect({ 
          type: 'achievement', 
          description: 'Score Master - 300+ Points!' 
        });
      }, 1000);
    }
  };

  const triggerEffect = (category, value) => {
    
    if (category === 'yahtzee' && value === 50) {
      setCurrentEffect({ type: 'yahtzee' });
      // Play excited cat sound
      playMeowExcited();
    } else if (category === 'fullHouse' && value === 25) {
      setCurrentEffect({ type: 'fullhouse' });
      // Play loud purring
      playPurrLoud();
    } else if ((category === 'largeStraight' && value === 40) || (category === 'smallStraight' && value === 30)) {
      setCurrentEffect({ type: 'straight' });
      // Play surprise meow
      playMeowSurprise();
    } else if (category === 'threeOfAKind' && value > 0) {
      setCurrentEffect({ type: 'kind', count: 3, emoji: '⚃' });
      // Play happy meow
      playMeowHappy();
    } else if (category === 'fourOfAKind' && value > 0) {
      setCurrentEffect({ type: 'kind', count: 4, emoji: '⚃' });
      // Play content purring
      playPurrContent();
    }
  };

  const scoreCategory = (category, player, value) => {
    // Store the current state for undo functionality
    setLastScoringAction({
      category,
      player,
      value,
      gameState: { ...gameState }
    });

    // Trigger special effects for scoring moments
    triggerEffect(category, value);
    
    setGameState(prevState => {
      const newState = { ...prevState };
      
      if (player === 1) {
        newState.player1Score = { ...newState.player1Score, [category]: value };
      } else {
        newState.player2Score = { ...newState.player2Score, [category]: value };
      }

      // Switch to next player or check if game over
      if (player === 1) {
        newState.currentPlayer = 2;
        // Play happy meow when switching to player 2
        setTimeout(() => {
          playMeowHappy();
        }, 100);
      } else {
        newState.currentPlayer = 1;
        // Play happy meow when switching to player 1
        setTimeout(() => {
          playMeowHappy();
        }, 100);
      }

      // Reset dice and rolls for next turn
      newState.dice = Array(5).fill(null);
      newState.held = Array(5).fill(false);
      newState.rollsLeft = 3;
      newState.hasRolled = false;

      // Check for achievements
      const newTotal = getTotalScore(player);
      checkAchievements(newTotal);

      // Check if game is over (all categories filled for both players)
      const categories = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes', 
                         'threeOfAKind', 'fourOfAKind', 'fullHouse', 'smallStraight', 
                         'largeStraight', 'yahtzee', 'chance'];
      
      const player1Complete = categories.every(cat => newState.player1Score[cat] !== undefined);
      const player2Complete = categories.every(cat => newState.player2Score[cat] !== undefined);
      
      if (player1Complete && player2Complete) {
        newState.gameOver = true;
        // Play content purr when game ends
        setTimeout(() => {
          playPurrContent();
        }, 500);
      }

      return newState;
    });
  };

  const calculateScore = (category) => {
    const dice = gameState.dice.filter(die => die !== null);
    if (dice.length === 0) return 0;
    
    const counts = Array(7).fill(0);
    
    dice.forEach(die => {
      counts[die]++;
    });

    switch(category) {
      case 'ones':
        return counts[1] * 1;
      case 'twos':
        return counts[2] * 2;
      case 'threes':
        return counts[3] * 3;
      case 'fours':
        return counts[4] * 4;
      case 'fives':
        return counts[5] * 5;
      case 'sixes':
        return counts[6] * 6;
      case 'threeOfAKind':
        return counts.some(count => count >= 3) ? dice.reduce((sum, die) => sum + die, 0) : 0;
      case 'fourOfAKind':
        return counts.some(count => count >= 4) ? dice.reduce((sum, die) => sum + die, 0) : 0;
      case 'fullHouse':
        const hasThree = counts.some(count => count === 3);
        const hasTwo = counts.some(count => count === 2);
        return (hasThree && hasTwo) ? 25 : 0;
      case 'smallStraight': {
        // Check for 1-2-3-4 or 2-3-4-5 or 3-4-5-6
        const hasStraight = 
          (counts[1] >= 1 && counts[2] >= 1 && counts[3] >= 1 && counts[4] >= 1) ||
          (counts[2] >= 1 && counts[3] >= 1 && counts[4] >= 1 && counts[5] >= 1) ||
          (counts[3] >= 1 && counts[4] >= 1 && counts[5] >= 1 && counts[6] >= 1);
        return hasStraight ? 30 : 0;
      }
      case 'largeStraight': {
        // Check for 1-2-3-4-5 or 2-3-4-5-6
        const hasStraight = 
          (counts[1] === 1 && counts[2] === 1 && counts[3] === 1 && counts[4] === 1 && counts[5] === 1) ||
          (counts[2] === 1 && counts[3] === 1 && counts[4] === 1 && counts[5] === 1 && counts[6] === 1);
        return hasStraight ? 40 : 0;
      }
      case 'yahtzee':
        return counts.some(count => count === 5) ? 50 : 0;
      case 'chance':
        return dice.reduce((sum, die) => sum + die, 0);
      default:
        return 0;
    }
  };

  const getTotalScore = (player) => {
    const scores = player === 1 ? gameState.player1Score : gameState.player2Score;
    let total = Object.values(scores).reduce((sum, score) => sum + score, 0);
    
    // Check for upper section bonus (if sum of ones through sixes is at least 63, add 35 bonus points)
    const upperSectionSum = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes']
      .reduce((sum, category) => sum + (scores[category] || 0), 0);
    
    if (upperSectionSum >= 63) {
      total += 35;
    }
    
    return total;
  };


  const currentTheme = backgroundThemes[backgroundTheme];

  // Cat transformation images for background madness
  const catTransformations = currentTheme.cats;

  return (
    <div className="app" style={{ background: currentTheme.backgroundColor }}>
      {/* THEMED BACKGROUND MADNESS */}
      <div className={`floating-madness theme-${backgroundTheme}`}>
        {/* Cats */}
        {Array.from({ length: 30 }, (_, i) => (
          <img 
            key={`cat-${i}`}
            src={catTransformations[i % catTransformations.length]}
            alt="Theme cat"
            className={`madness-cat cat-${currentTheme.pattern} madness-${i % 20}`}
          />
        ))}
        {/* Emojis */}
        {Array.from({ length: 25 }, (_, i) => (
          <div 
            key={`emoji-${i}`}
            className={`madness-emoji emoji-${currentTheme.pattern} emoji-${i % 15}`}
          >
            {currentTheme.emojis[i % currentTheme.emojis.length]}
          </div>
        ))}
      </div>
      
      {!isMobile && (
        <header>
          <h1 
            onClick={() => playPurrLoud()}
            style={{ cursor: 'pointer' }}
          >
            🐱 Catzee 🎲
          </h1>
          <div className="header-buttons">
            <button onClick={() => setShowHelp(true)} className="help-btn">❓ Help</button>
            {lastScoringAction && (
              <button 
                onClick={undoLastScore}
                className="undo-btn"
                title="Undo last score selection"
              >
                Undo
              </button>
            )}
            <button onClick={resetGame} className="new-game-btn">New Game</button>
          </div>
        </header>
      )}

      <div className="game-container">
        <div className="scorecards">
          <ScoreCard 
            title="Player 1"
            active={gameState.currentPlayer === 1}
            scores={gameState.player1Score}
            dice={gameState.dice}
            onScore={(category) => scoreCategory(category, 1, calculateScore(category))}
            calculateScore={calculateScore}
            totalScore={getTotalScore(1)}
            rollsLeft={gameState.rollsLeft}
            playerNumber={1}
          />
          <ScoreCard 
            title="Player 2"
            active={gameState.currentPlayer === 2}
            scores={gameState.player2Score}
            dice={gameState.dice}
            onScore={(category) => scoreCategory(category, 2, calculateScore(category))}
            calculateScore={calculateScore}
            totalScore={getTotalScore(2)}
            rollsLeft={gameState.rollsLeft}
            playerNumber={2}
          />
        </div>
      </div>

      <GameBoard 
        dice={gameState.dice}
        held={gameState.held}
        rollsLeft={gameState.rollsLeft}
        onRoll={rollDice}
        onToggleHold={toggleHold}
        isMobile={isMobile}
        onShowMobileMenu={() => setShowMobileOptions(true)}
      />

      {gameState.gameOver && (
        <div className="game-over-overlay">
          <div className="game-over-modal">
            <h2>Game Over!</h2>
            <p>Player 1: {getTotalScore(1)} points</p>
            <p>Player 2: {getTotalScore(2)} points</p>
            <h3>{getTotalScore(1) > getTotalScore(2) ? 'Player 1 Wins!' : 
                getTotalScore(2) > getTotalScore(1) ? 'Player 2 Wins!' : 'It\'s a Tie!'}</h3>
            <button onClick={resetGame}>Play Again</button>
          </div>
        </div>
      )}

      <EffectsOverlay 
        effect={currentEffect} 
        onComplete={handleEffectComplete} 
      />

      <HelpDialog 
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

      <MobileOptionsModal
        isOpen={showMobileOptions}
        onClose={() => setShowMobileOptions(false)}
        onHelp={handleMobileHelp}
        onUndo={handleMobileUndo}
        onNewGame={handleMobileNewGame}
        canUndo={lastScoringAction !== null}
      />
    </div>
  );
}

export default App;